import { useIsDesktop } from "@agrigate/shared-web-ui/lib/hooks/use-is-desktop";
import type {
    MenuItemConfig,
    MenuSeparatorConfig,
} from "@agrigate/shared-web-ui/lib/PopoverMenuCombo";
import PopoverMenuCombo from "@agrigate/shared-web-ui/lib/PopoverMenuCombo";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useAtom } from "jotai";
// Header.tsx
import { type FC, type ReactNode, useMemo } from "react";
import { FiUser } from "react-icons/fi";
import { RxHamburgerMenu } from "react-icons/rx";
import { usePromiseToast } from "../hooks/use-promise-toast";
import { sessionAtom } from "../stores/session";
import rpc from "../utils/rpc";
import GlobalSearch from "./GlobalSearch";

interface HeaderProps {
    title: string | undefined;
    icon?: ReactNode;
    notificationCount?: number;
    onMenuClick?: () => void;
    isMenuOpen?: boolean;
}

const ContentHeader: FC<HeaderProps> = ({
    title,
    icon,
    onMenuClick,
    isMenuOpen,
}) => {
    const [session, setSession] = useAtom(sessionAtom);
    const isAdmin = session?.roles?.includes("ADMIN");
    const isDesktop = useIsDesktop();
    const { withToast } = usePromiseToast();

    const emailHeaderOptions: (MenuItemConfig | MenuSeparatorConfig)[] =
        useMemo(
            () => [
                {
                    type: "item",
                    key: "email",
                    label: session?.emails?.[0] ?? "Guest",
                },
                {
                    type: "separator",
                    key: "sep",
                },
            ],
            [session?.emails?.[0]],
        );

    const menuOptions: (MenuItemConfig | MenuSeparatorConfig)[] =
        useMemo(() => {
            if (isAdmin)
                return [
                    ...emailHeaderOptions,
                    {
                        type: "item",
                        key: "logout",
                        label: "Logout",
                        onClick: () => {
                            setSession(null);
                            if (LOGOUT_URL) {
                                location.href = LOGOUT_URL;
                            }
                        },
                    },
                ];
            else
                return [
                    ...emailHeaderOptions,
                    {
                        type: "item",
                        key: "deleteSelf",
                        label: "Delete Account",
                        onClick: () => {
                            withToast(
                                async () => {
                                    await rpc.users.deleteMany({
                                        userIds: ["$self"],
                                    });
                                    setSession(null);
                                },
                                {
                                    messages: {
                                        success: "Account deleted successfully",
                                        error: "Failed to process your request",
                                    },
                                },
                            );
                        },
                    },
                    {
                        type: "item",
                        key: "logout",
                        label: "Logout",
                        onClick: () => {
                            setSession(null);
                        },
                    },
                ];
        }, [isAdmin, emailHeaderOptions]);

    return (
        <Flex
            as="header"
            direction="column"
            borderBottomWidth={1}
            borderColor="gray.100"
            backgroundColor={"bg"}
            flexShrink={0}
            className="screen-only"
        >
            <Flex align="center" justify="space-between" px={4} height="50px">
                <Flex align="center">
                    {!isDesktop && (
                        <Button
                            variant="outline"
                            p={2}
                            mr={2}
                            onClick={onMenuClick}
                        >
                            <RxHamburgerMenu />
                        </Button>
                    )}

                    {icon && <Box mr={2}>{icon}</Box>}
                    <Text fontSize="sm" fontWeight="bold" textAlign="left">
                        {title ?? "Dashboard"}
                    </Text>
                </Flex>
                {isDesktop && <GlobalSearch />}
                <Flex gap={2} alignItems="center">
                    {/* <NotificationBadge
                        badgeColor="red.500"
                        hasNotification
                        iconSize={34}
                        count={99}
                    />
                    <Avatar
                        size={"s"}
                        src={ProfileAvatarIcon}
                    /> */}
                    <PopoverMenuCombo
                        size="sm"
                        variant="plain"
                        label={
                            <Box
                                borderRadius="15px"
                                p="5px"
                                border="1px solid var(--chakra-colors-gray-500)"
                            >
                                <FiUser />
                            </Box>
                        }
                        options={menuOptions}
                    />
                </Flex>
            </Flex>
            {!isDesktop && !isMenuOpen && (
                <Flex px={4} pb={2} width="100%">
                    <GlobalSearch width="100%" />
                </Flex>
            )}
        </Flex>
    );
};

export default ContentHeader;
